import { useRef, useState } from "react";
import { Trash2, Plus, Loader2, Image as ImageIcon, FileText } from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { isPdf, readFileAsDataURL, downsizeImage } from "@/lib/utils";
import { uploadImage, analyzeImage } from "@/api";
import PdfThumbnail from "./PdfThumbnail";

/**
 * Click anywhere on the plan to drop a pin.
 * Drop an image (or PDF) file directly to upload it and create a pin at the drop position.
 * Each pin can be labeled and linked to any image in the project.
 *
 * Props:
 *   floorplan, image, images, projectId
 *   onChange(pins)        – called when pins array changes
 *   onDelete()            – delete the whole floorplan
 *   onUploaded(image)     – called when a new image is uploaded via drop (so parent can refresh images)
 */
export default function FloorplanEditor({
  floorplan,
  image,
  images,
  projectId,
  onChange,
  onDelete,
  onUploaded,
}) {
  const wrapRef = useRef(null);
  const [pins, setPins] = useState(floorplan.pins || []);
  const [active, setActive] = useState(null);
  const [hoverPin, setHoverPin] = useState(null);
  const [linkPicker, setLinkPicker] = useState(null); // pin id when picking from grid
  const [dropOver, setDropOver] = useState(false);
  const [dropBusy, setDropBusy] = useState(false);

  function commit(next) {
    setPins(next);
    onChange?.(next);
  }

  function handleAdd(e) {
    if (e.target !== wrapRef.current && !e.target.classList.contains("fp-img"))
      return;
    const rect = wrapRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    const next = [
      ...pins,
      {
        id: `${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
        x,
        y,
        label: "",
        note: "",
        image_id: null,
      },
    ];
    commit(next);
    setActive(next[next.length - 1].id);
  }

  function updatePin(id, patch) {
    commit(pins.map((p) => (p.id === id ? { ...p, ...patch } : p)));
  }

  function removePin(id) {
    commit(pins.filter((p) => p.id !== id));
    setActive(null);
  }

  // ---- Drop image directly on canvas ----
  function hasDraggedFiles(e) {
    return Array.from(e.dataTransfer?.types || []).includes("Files");
  }
  function onCanvasDragEnter(e) {
    if (!hasDraggedFiles(e)) return;
    e.preventDefault();
    setDropOver(true);
  }
  function onCanvasDragOver(e) {
    if (!hasDraggedFiles(e)) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "copy";
  }
  function onCanvasDragLeave(e) {
    if (e.target === wrapRef.current) setDropOver(false);
  }
  async function onCanvasDrop(e) {
    if (!hasDraggedFiles(e)) return;
    e.preventDefault();
    e.stopPropagation();
    setDropOver(false);
    const files = Array.from(e.dataTransfer.files || []);
    if (files.length === 0) return;
    const rect = wrapRef.current.getBoundingClientRect();
    const baseX = ((e.clientX - rect.left) / rect.width) * 100;
    const baseY = ((e.clientY - rect.top) / rect.height) * 100;
    setDropBusy(true);
    const newPins = [];
    for (let i = 0; i < files.length; i++) {
      const f = files[i];
      const filePdf = f.type === "application/pdf" || /\.pdf$/i.test(f.name);
      const isImg = (f.type || "").startsWith("image/");
      if (!filePdf && !isImg) {
        toast.error(`Unsupported: ${f.name}`);
        continue;
      }
      try {
        const raw = await readFileAsDataURL(f);
        const data = filePdf ? raw : await downsizeImage(raw, 1600, 0.85);
        const created = await uploadImage(projectId, {
          project_id: projectId,
          data,
          filename: f.name,
          category: "inspiration",
          mime: filePdf ? "application/pdf" : f.type || "image/jpeg",
        });
        onUploaded?.(created);
        // Slight offset so multiple drops don't stack perfectly
        const offset = i * 1.5;
        newPins.push({
          id: `${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
          x: Math.min(100, Math.max(0, baseX + offset)),
          y: Math.min(100, Math.max(0, baseY + offset)),
          label: "",
          note: "",
          image_id: created.id,
        });
        if (!filePdf) {
          analyzeImage({ image_data: data, image_id: created.id })
            .then((res) => onUploaded?.({ ...created, ...res, ai_analyzed: true }))
            .catch(() => {});
        }
      } catch (err) {
        console.error(err);
        toast.error(`Failed: ${f.name}`);
      }
    }
    setDropBusy(false);
    if (newPins.length) {
      commit([...pins, ...newPins]);
      toast.success(
        `${newPins.length} pin${newPins.length === 1 ? "" : "s"} added at drop point`
      );
    }
  }

  const linkablePool = images.filter(
    (i) => i.category !== "floorplan" && i.id !== image.id
  );

  return (
    <div className="reveal" data-testid={`floorplan-${floorplan.id}`}>
      <div className="flex items-center justify-between mb-3 gap-3">
        <div>
          <p className="text-xs uppercase tracking-[0.15em] text-stone-500 font-semibold">
            Floorplan
          </p>
          <h3 className="font-display text-xl">
            {image.title || image.filename || "Plan"} · {pins.length} pin
            {pins.length === 1 ? "" : "s"}
          </h3>
        </div>
        <Button
          variant="ghost"
          onClick={onDelete}
          data-testid={`delete-floorplan-${floorplan.id}`}
          className="text-stone-500 hover:text-red-600"
        >
          <Trash2 className="w-4 h-4 mr-2" strokeWidth={1.5} />
          Remove
        </Button>
      </div>
      <div
        ref={wrapRef}
        onClick={handleAdd}
        onDragEnter={onCanvasDragEnter}
        onDragOver={onCanvasDragOver}
        onDragLeave={onCanvasDragLeave}
        onDrop={onCanvasDrop}
        className="relative rounded-2xl overflow-hidden bg-stone-50 cursor-crosshair select-none"
        style={{ aspectRatio: "16/10" }}
        data-testid={`floorplan-canvas-${floorplan.id}`}
      >
        <img
          src={image.data}
          alt="floorplan"
          className="fp-img absolute inset-0 w-full h-full object-contain pointer-events-none"
          draggable={false}
        />
        {pins.map((pin, idx) => {
          const linkedImg = pin.image_id
            ? images.find((i) => i.id === pin.image_id)
            : null;
          const hasLabel = (pin.label || "").trim().length > 0;
          const linkedIsPdf = linkedImg ? isPdf(linkedImg) : false;
          const showHover =
            hoverPin === pin.id && active !== pin.id && (linkedImg || hasLabel);
          return (
            <Popover
              key={pin.id}
              open={active === pin.id}
              onOpenChange={(o) => setActive(o ? pin.id : null)}
            >
              <PopoverTrigger asChild>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setActive(pin.id);
                  }}
                  onMouseEnter={() => setHoverPin(pin.id)}
                  onMouseLeave={() =>
                    setHoverPin((curr) => (curr === pin.id ? null : curr))
                  }
                  data-testid={`pin-${pin.id}`}
                  className="absolute -translate-x-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-stone-900 text-white text-xs font-semibold flex items-center justify-center shadow-md border-2 border-white hover:scale-110 transition-transform"
                  style={{ left: `${pin.x}%`, top: `${pin.y}%` }}
                >
                  {idx + 1}
                </button>
              </PopoverTrigger>
              {showHover && (
                <div
                  data-testid={`pin-hover-preview-${pin.id}`}
                  className="absolute z-20 pointer-events-none -translate-x-1/2 animate-in fade-in zoom-in-95 duration-150"
                  style={{
                    left: `${pin.x}%`,
                    top: `calc(${pin.y}% + 24px)`,
                  }}
                >
                  {linkedImg ? (
                    <div className="bg-white rounded-lg shadow-xl border border-stone-200 p-1.5 w-40">
                      {linkedIsPdf ? (
                        <div className="w-full h-24 rounded overflow-hidden">
                          <PdfThumbnail
                            src={linkedImg.data}
                            label={linkedImg.filename || "PDF"}
                          />
                        </div>
                      ) : (
                        <img
                          src={linkedImg.data}
                          alt={linkedImg.title || "linked"}
                          className="w-full h-24 object-cover rounded"
                          draggable={false}
                        />
                      )}
                      {(pin.label || linkedImg.title) && (
                        <div className="px-1 pt-1.5 pb-0.5 text-[11px] font-medium text-stone-700 truncate">
                          {pin.label || linkedImg.title}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="bg-stone-900 text-white rounded-lg shadow-xl px-3 py-1.5 text-xs font-medium max-w-[220px]">
                      {pin.label}
                    </div>
                  )}
                </div>
              )}
              <PopoverContent
                className="w-80 p-4"
                data-testid={`pin-popover-${pin.id}`}
                onClick={(e) => e.stopPropagation()}
              >
                <div className="space-y-3">
                  <div>
                    <label className="text-[10px] uppercase tracking-wider text-stone-500">
                      Label
                    </label>
                    <Input
                      value={pin.label || ""}
                      onChange={(e) =>
                        updatePin(pin.id, { label: e.target.value })
                      }
                      placeholder="e.g. Sofa, Light fitting"
                      data-testid={`pin-label-${pin.id}`}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <label className="text-[10px] uppercase tracking-wider text-stone-500">
                      Linked image
                    </label>
                    {linkedImg ? (
                      <div className="mt-1 flex items-center gap-2 p-2 rounded-md bg-stone-50 border border-stone-200">
                        <div className="w-12 h-12 rounded overflow-hidden bg-stone-100 shrink-0">
                          {linkedIsPdf ? (
                            <PdfThumbnail
                              src={linkedImg.data}
                              label={linkedImg.filename || "PDF"}
                            />
                          ) : (
                            <img
                              src={linkedImg.data}
                              alt=""
                              className="w-full h-full object-cover"
                            />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-xs font-medium text-stone-800 truncate">
                            {linkedImg.title || linkedImg.filename || "Image"}
                          </div>
                          <div className="flex gap-2 mt-0.5">
                            <button
                              type="button"
                              onClick={() => setLinkPicker(pin.id)}
                              data-testid={`pin-relink-${pin.id}`}
                              className="text-[11px] text-stone-600 hover:text-stone-900 underline-offset-2 hover:underline"
                            >
                              Change
                            </button>
                            <button
                              type="button"
                              onClick={() =>
                                updatePin(pin.id, { image_id: null })
                              }
                              data-testid={`pin-unlink-${pin.id}`}
                              className="text-[11px] text-red-600 hover:text-red-700 underline-offset-2 hover:underline"
                            >
                              Unlink
                            </button>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => setLinkPicker(pin.id)}
                        data-testid={`pin-link-button-${pin.id}`}
                        className="mt-1 w-full justify-start rounded-md"
                      >
                        <ImageIcon
                          className="w-3.5 h-3.5 mr-2"
                          strokeWidth={1.5}
                        />
                        Link an image…
                      </Button>
                    )}
                  </div>
                  {linkPicker === pin.id && (
                    <ImagePickerGrid
                      images={linkablePool}
                      onPick={(img) => {
                        updatePin(pin.id, { image_id: img.id });
                        setLinkPicker(null);
                      }}
                      onClose={() => setLinkPicker(null)}
                      testidPrefix={`pin-${pin.id}`}
                    />
                  )}
                  <div className="flex justify-between pt-1">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => removePin(pin.id)}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      data-testid={`remove-pin-${pin.id}`}
                    >
                      <Trash2 className="w-3.5 h-3.5 mr-1" />
                      Remove
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      onClick={() => setActive(null)}
                      className="rounded-full bg-stone-900 text-white hover:bg-stone-800"
                      data-testid={`close-pin-${pin.id}`}
                    >
                      Done
                    </Button>
                  </div>
                </div>
              </PopoverContent>
            </Popover>
          );
        })}
        {(dropOver || dropBusy) && (
          <div className="absolute inset-0 z-30 flex items-center justify-center bg-stone-900/40 backdrop-blur-sm pointer-events-none">
            <div className="bg-white rounded-2xl shadow-2xl px-6 py-4 flex items-center gap-3">
              {dropBusy ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin text-stone-900" />
                  <span className="font-display text-lg text-stone-900">
                    Adding pin…
                  </span>
                </>
              ) : (
                <>
                  <Plus className="w-5 h-5 text-stone-900" />
                  <span className="font-display text-lg text-stone-900">
                    Drop to pin at this spot
                  </span>
                </>
              )}
            </div>
          </div>
        )}
        {pins.length === 0 && !dropOver && (
          <div className="absolute inset-x-0 bottom-3 flex justify-center pointer-events-none">
            <span className="bg-white/95 backdrop-blur text-stone-700 text-xs px-3 py-1.5 rounded-full flex items-center gap-1.5">
              <Plus className="w-3 h-3" strokeWidth={1.5} />
              Click — or drop an image — to add a pin
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

function ImagePickerGrid({ images, onPick, onClose, testidPrefix }) {
  if (images.length === 0) {
    return (
      <div className="text-xs text-stone-500 py-3 text-center">
        No images yet in this project.
      </div>
    );
  }
  return (
    <div className="border border-stone-200 rounded-lg p-2 bg-white">
      <div className="flex items-center justify-between mb-2 px-1">
        <span className="text-[10px] uppercase tracking-wider text-stone-500 font-semibold">
          Pick an image
        </span>
        <button
          type="button"
          onClick={onClose}
          className="text-[11px] text-stone-500 hover:text-stone-900"
        >
          Cancel
        </button>
      </div>
      <div
        className="grid grid-cols-4 gap-1.5 max-h-56 overflow-y-auto"
        data-testid={`${testidPrefix}-picker-grid`}
      >
        {images.map((img) => {
          const pdf = isPdf(img);
          return (
            <button
              key={img.id}
              type="button"
              onClick={() => onPick(img)}
              data-testid={`${testidPrefix}-picker-${img.id}`}
              className="aspect-square rounded overflow-hidden bg-stone-100 ring-0 hover:ring-2 hover:ring-stone-900 transition-all relative"
              title={img.title || img.filename || ""}
            >
              {pdf ? (
                <div className="w-full h-full">
                  <PdfThumbnail
                    src={img.data}
                    label={img.filename || "PDF"}
                  />
                </div>
              ) : (
                <img
                  src={img.data}
                  alt=""
                  className="w-full h-full object-cover"
                  draggable={false}
                />
              )}
              {pdf && (
                <span className="absolute top-0.5 left-0.5 bg-stone-900 text-white text-[8px] uppercase tracking-wider font-semibold px-1 rounded inline-flex items-center gap-0.5">
                  <FileText className="w-2 h-2" strokeWidth={2} />
                  PDF
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
